'use client';

import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import { ChevronDownIcon, X } from 'lucide-react';

import * as React from 'react';
import { forwardRef, useEffect } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/core/lib/utils';

export interface Option {
  value: string;
  label: string;
  disable?: boolean;
  /** fixed option that can't be removed. */
  fixed?: boolean;
  /** Group the options by providing key. */
  [key: string]: string | boolean | number | undefined | any;
}

interface GroupOption {
  [key: string]: Option[];
}

interface MultipleSelectorProps {
  value?: Option[];
  defaultOptions?: Option[];
  /** manually controlled options */
  options?: Option[];
  placeholder?: string;
  /** Loading component. */
  loadingIndicator?: React.ReactNode;
  /** Empty component. */
  emptyIndicator?: React.ReactNode;
  /** Debounce time for async search. Only work with `onSearch` or `fetchFunction`. */
  delay?: number;
  /**
   * Only work with `onSearch` or `fetchFunction` prop. Trigger search when `onFocus`.
   **/
  triggerSearchOnFocus?: boolean;
  /** async search (Legacy) */
  onSearch?: (value: string) => Promise<Option[]>;

  // ==========================================
  // NEW PROPS FOR DIRECT API FETCHING
  // ==========================================
  /** Function to fetch data directly from API based on query */
  fetchFunction?: (query: string) => Promise<any[]>;
  /** The key from your API response object to use as the Option 'value' (Default: 'id') */
  valueKey?: string;
  /** The key from your API response object to use as the Option 'label' (Default: 'title') */
  labelKey?: string;
  // ==========================================
  label?: string;
  /** sync search */
  onSearchSync?: (value: string) => Option[];
  onChange?: (options: Option[]) => void;
  maxSelected?: number;
  onMaxSelected?: (maxLimit: number) => void;
  hidePlaceholderWhenSelected?: boolean;
  disabled?: boolean;
  groupBy?: string;
  className?: string;
  badgeClassName?: string;
  selectFirstItem?: boolean;
  creatable?: boolean;
  commandProps?: React.ComponentPropsWithoutRef<typeof Command>;
  inputProps?: Omit<
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
    'value' | 'placeholder' | 'disabled'
  >;
  hideClearAllButton?: boolean;
}

export interface MultipleSelectorRef {
  selectedValue: Option[];
  input: HTMLInputElement;
  focus: () => void;
  reset: () => void;
}

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function transToGroupOption(options: Option[], groupBy?: string) {
  if (options.length === 0) {
    return {};
  }
  if (!groupBy) {
    return {
      '': options,
    };
  }

  const groupOption: GroupOption = {};
  options.forEach(option => {
    const key = (option[groupBy] as string) || '';
    if (!groupOption[key]) {
      groupOption[key] = [];
    }
    groupOption[key].push(option);
  });
  return groupOption;
}

function removePickedOption(groupOption: GroupOption, picked: Option[]) {
  const cloneOption = JSON.parse(JSON.stringify(groupOption)) as GroupOption;

  for (const [key, value] of Object.entries(cloneOption)) {
    cloneOption[key] = value.filter(
      val => !picked.find(p => String(p.value) === String(val.value))
    );
  }
  return cloneOption;
}

function isOptionsExist(groupOption: GroupOption, targetOption: Option[]) {
  for (const [, value] of Object.entries(groupOption)) {
    if (
      value.some(option => targetOption.find(p => String(p.value) === String(option.value)))
    ) {
      return true;
    }
  }
  return false;
}

const CommandEmpty = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CommandPrimitive.Empty>
>(({ className, ...props }, forwardedRef) => {
  const render = useCommandState(state => state.filtered.count === 0);

  if (!render) return null;

  return (
    <div
      ref={forwardedRef}
      className={cn('py-6 text-center text-sm', className)}
      cmdk-empty=""
      role="presentation"
      {...props}
    />
  );
});

CommandEmpty.displayName = 'CommandEmpty';

const MultipleSelector = React.forwardRef<
  MultipleSelectorRef,
  MultipleSelectorProps
>(
  (
    {
      value,
      onChange,
      placeholder,
      defaultOptions: arrayDefaultOptions = [],
      options: arrayOptions,
      delay,
      onSearch,
      fetchFunction,
      valueKey = 'id',
      labelKey = 'title',
      label,
      onSearchSync,
      loadingIndicator,
      emptyIndicator,
      maxSelected = Number.MAX_SAFE_INTEGER,
      onMaxSelected,
      hidePlaceholderWhenSelected,
      disabled,
      groupBy,
      className,
      badgeClassName,
      selectFirstItem = true,
      creatable = false,
      triggerSearchOnFocus = false,
      commandProps,
      inputProps,
      hideClearAllButton = false,
    }: MultipleSelectorProps,
    ref: React.Ref<MultipleSelectorRef>
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [open, setOpen] = React.useState(false);
    const [onScrollbar, setOnScrollbar] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = React.useState<{
      top: number;
      left: number;
      width: number;
    } | null>(null);

    const [selected, setSelected] = React.useState<Option[]>(value || []);
    const [options, setOptions] = React.useState<GroupOption>(
      transToGroupOption(arrayDefaultOptions, groupBy)
    );
    const [inputValue, setInputValue] = React.useState('');
    const debouncedSearchTerm = useDebounce(inputValue, delay || 500);

    const isAsync = !!onSearch || !!fetchFunction;
    const isPending = isAsync && (isLoading || inputValue !== debouncedSearchTerm);

    React.useImperativeHandle(
      ref,
      () => ({
        selectedValue: [...selected],
        input: inputRef.current as HTMLInputElement,
        focus: () => inputRef?.current?.focus(),
        reset: () => setSelected([]),
      }),
      [selected]
    );

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        inputRef.current.blur();
      }
    };

    const handleUnselect = React.useCallback(
      (option: Option) => {
        const newOptions = selected.filter(s => String(s.value) !== String(option.value));
        setSelected(newOptions);
        onChange?.(newOptions);
      },
      [onChange, selected]
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            if (input.value === '' && selected.length > 0) {
              const lastSelectOption = selected[selected.length - 1];
              if (lastSelectOption && !lastSelectOption.fixed) {
                handleUnselect(lastSelectOption);
              }
            }
          }
          if (e.key === 'Escape') {
            input.blur();
          }
        }
      },
      [handleUnselect, selected]
    );

    useEffect(() => {
      if (!open) return;
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, { passive: true });
      return () => window.removeEventListener('scroll', updatePosition);
    }, [open, inputRef]);

    useEffect(() => {
      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchend', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchend', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchend', handleClickOutside);
      };
    }, [open]);

    useEffect(() => {
      if (value) {
        setSelected(value);
      }
    }, [value]);

    useEffect(() => {
      if (!arrayOptions || onSearch || fetchFunction) {
        return;
      }
      const newOption = transToGroupOption(arrayOptions || [], groupBy);
      if (JSON.stringify(newOption) !== JSON.stringify(options)) {
        setOptions(newOption);
      }
    }, [arrayDefaultOptions, arrayOptions, groupBy, onSearch, fetchFunction, options]);

    useEffect(() => {
      const doSearchSync = () => {
        const res = onSearchSync?.(debouncedSearchTerm);
        setOptions(transToGroupOption(res || [], groupBy));
      };

      const exec = async () => {
        if (!onSearchSync || !open) return;

        if (triggerSearchOnFocus) {
          doSearchSync();
        }

        if (debouncedSearchTerm) {
          doSearchSync();
        }
      };

      void exec();
    }, [debouncedSearchTerm, groupBy, open, triggerSearchOnFocus]);

    useEffect(() => {
      const doSearch = async () => {
        setIsLoading(true);
        try {
          let res: Option[] = [];

          if (fetchFunction) {
            const rawData = await fetchFunction(debouncedSearchTerm);
            res = rawData.map((item: any) => ({
              label: String(item[labelKey] || item.label || ''),
              value: String(item[valueKey] || item.value || ''),
              ...item,
            }));
          } else if (onSearch) {
            res = (await onSearch?.(debouncedSearchTerm)) || [];
          }

          setOptions(transToGroupOption(res, groupBy));
        } catch (error) {
          console.error("Error fetching options:", error);
          setOptions({});
        } finally {
          setIsLoading(false);
        }
      };

      const exec = async () => {
        if ((!onSearch && !fetchFunction) || !open) return;

        if (!triggerSearchOnFocus && !debouncedSearchTerm) {
          setOptions({});
          return;
        }

        await doSearch();
      };

      void exec();
    }, [debouncedSearchTerm, groupBy, open, triggerSearchOnFocus, fetchFunction, labelKey, valueKey, onSearch]);

    const CreatableItem = () => {
      if (!creatable) return undefined;
      if (
        isOptionsExist(options, [{ value: inputValue, label: inputValue }]) ||
        selected.find(s => s.value === inputValue)
      ) {
        return undefined;
      }

      const Item = (
        <CommandItem
          value={inputValue}
          className="cursor-pointer"
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onSelect={(value: string) => {
            if (selected.length >= maxSelected) {
              onMaxSelected?.(selected.length);
              return;
            }
            // NEW: این خط حذف/کامنت شد تا متن سرچ کاربر پاک نشود
            // setInputValue(''); 

            const newOptions = [...selected, { value, label: value }];
            setSelected(newOptions);
            onChange?.(newOptions);

            // NEW: اطمینان از باز ماندن منو با فوکوس مجدد روی اینپوت
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0);
          }}
        >
          {`Create "${inputValue}"`}
        </CommandItem>
      );

      if (!onSearch && !fetchFunction && inputValue.length > 0) {
        return Item;
      }

      if ((onSearch || fetchFunction) && debouncedSearchTerm.length > 0 && !isLoading) {
        return Item;
      }

      return undefined;
    };

    const EmptyItem = React.useCallback(() => {
      if (!emptyIndicator) return undefined;

      if ((onSearch || fetchFunction) && !creatable && Object.keys(options).length === 0) {
        return (
          <CommandItem value="-" disabled>
            {emptyIndicator}
          </CommandItem>
        );
      }

      return <CommandEmpty>{emptyIndicator}</CommandEmpty>;
    }, [creatable, emptyIndicator, onSearch, fetchFunction, options]);

    const selectables = React.useMemo<GroupOption>(
      () => removePickedOption(options, selected),
      [options, selected]
    );

    const commandFilter = React.useCallback(() => {
      if (commandProps?.filter) {
        return commandProps.filter;
      }

      if (creatable) {
        return (value: string, search: string) => {
          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : -1;
        };
      }
      return undefined;
    }, [creatable, commandProps?.filter]);

    return (
      <div className="space-y-2">  {/* WRAPPER DIV */}
        {/* Label */}
        {label && <label className={'text-primary-600 font-bold'}>{label}</label>}
        <Command
          ref={dropdownRef}
          {...commandProps}
          onKeyDown={e => {
            handleKeyDown(e);
            commandProps?.onKeyDown?.(e);
          }}
          className={cn(
            'h-auto overflow-visible bg-transparent',
            commandProps?.className
          )}
          shouldFilter={
            commandProps?.shouldFilter !== undefined
              ? commandProps.shouldFilter
              : (!onSearch && !fetchFunction)
          }
          filter={commandFilter()}
        >
          <div
            className={cn(
              'flex items-start justify-between rounded-md border border-input px-3 py-2 text- ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 md:text-sm',
              {
                'cursor-text': !disabled && selected.length !== 0,
              },
              className
            )}
            onClick={() => {
              if (disabled) return;
              inputRef?.current?.focus();
            }}
          >
            <div className="relative flex flex-wrap gap-1">
              {selected.map(option => {
                return (
                  <Badge
                    key={option.value}
                    className={cn(
                      'gap-1 py-0.5 pe-1 ps-2',
                      'data-[disabled]:bg-muted-foreground data-[disabled]:text-muted data-[disabled]:hover:bg-muted-foreground',
                      'data-[fixed]:bg-muted-foreground data-[fixed]:text-muted data-[fixed]:hover:bg-muted-foreground',
                      badgeClassName
                    )}
                    variant="outline"
                    data-fixed={option.fixed}
                    data-disabled={disabled || undefined}
                  >
                    {option.label}
                    <button
                      type="button"
                      className={cn(
                        'flex items-center justify-center rounded-full p-0.5 outline-none ring-offset-background transition-colors hover:bg-muted-foreground/15 focus:ring-2 focus:ring-ring focus:ring-offset-2',
                        (disabled || option.fixed) && 'hidden'
                      )}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          handleUnselect(option);
                        }
                      }}
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => handleUnselect(option)}
                    >
                      <X className="h-2.5 w-2.5 shrink-0 text-muted-foreground hover:text-foreground" strokeWidth={2.5} />
                    </button>
                  </Badge>
                );
              })}
              <CommandPrimitive.Input
                {...inputProps}
                ref={inputRef}
                value={inputValue}
                disabled={disabled}
                onValueChange={value => {
                  setInputValue(value);
                  inputProps?.onValueChange?.(value);
                }}
                onBlur={event => {
                  if (!onScrollbar) {
                    setOpen(false);
                  }
                  inputProps?.onBlur?.(event);
                }}
                onFocus={event => {
                  setOpen(true);
                  inputProps?.onFocus?.(event);
                }}
                placeholder={
                  hidePlaceholderWhenSelected && selected.length !== 0
                    ? ''
                    : placeholder
                }
                className={cn(
                  'flex-1 self-baseline bg-transparent outline-none placeholder:text-muted-foreground',
                  {
                    'w-full': hidePlaceholderWhenSelected,
                  },
                  inputProps?.className
                )}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setSelected(selected.filter(s => s.fixed));
                onChange?.(selected.filter(s => s.fixed));
              }}
              className={cn(
                'size-4',
                (hideClearAllButton ||
                  disabled ||
                  selected.length < 1 ||
                  selected.filter(s => s.fixed).length === selected.length) &&
                'hidden'
              )}
            >
              <X className="stroke-1" />
            </button>
            <ChevronDownIcon
              className={cn(
                'size-3 text-muted-foreground/50',
                (hideClearAllButton ||
                  disabled ||
                  selected.length >= 1 ||
                  selected.filter(s => s.fixed).length !== selected.length) &&
                'hidden'
              )}
            />
          </div>
          <div className="relative">
            {open && (
              <CommandList
                className="text-xs! absolute top-1 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
                onMouseLeave={() => {
                  setOnScrollbar(false);
                }}
                onMouseEnter={() => {
                  setOnScrollbar(true);
                }}
                onMouseUp={() => {
                  inputRef?.current?.focus();
                }}
              >
                {isPending ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {loadingIndicator || "در حال جستجو..."}
                  </div>
                ) : (
                  <>
                    {EmptyItem()}
                    {CreatableItem()}
                    {!selectFirstItem && (
                      <CommandItem value="-" className="hidden" />
                    )}
                    {Object.entries(selectables).map(([key, dropdowns]) => (
                      <CommandGroup
                        key={key}
                        heading={key}
                        className="h-full overflow-auto"
                      >
                        <>
                          {dropdowns.map(option => {
                            return (
                              <CommandItem
                                key={option.value}
                                value={option.label}
                                disabled={option.disable}
                                onMouseDown={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onSelect={() => {
                                  if (selected.length >= maxSelected) {
                                    onMaxSelected?.(selected.length);
                                    return;
                                  }

                                  setInputValue('');
                                  // NEW: این خط حذف/کامنت شد تا متن جستجو پاک نشود
                                  // setInputValue(''); 

                                  const newOptions = [...selected, option];
                                  setSelected(newOptions);
                                  onChange?.(newOptions);

                                  // NEW: حفظ فوکوس روی اینپوت با تاخیر صفر تا درآپ‌داون بسته نشود
                                  setTimeout(() => {
                                    inputRef.current?.focus();
                                  }, 0);
                                }}
                                className={cn(
                                  'cursor-pointer',
                                  option.disable &&
                                  'cursor-default text-muted-foreground'
                                )}
                              >
                                {option.label}
                              </CommandItem>
                            );
                          })}
                        </>
                      </CommandGroup>
                    ))}
                  </>
                )}
              </CommandList>
            )}
          </div>
        </Command>
      </div>
    );
  }
);

MultipleSelector.displayName = 'MultipleSelector';
export default MultipleSelector;